// @lat: [[frontend/sc-frontend#Design System and Tokens#Hero backgrounds]]
export type HeroBackgroundTheme = "signals" | "helix" | "cells" | "neurons" | "still";

/** The two planes a scene paints. `far` sits behind a lens blur, so a scene
 *  reads with depth of field; `near` is in focus. */
export interface HeroLayers {
  far: CanvasRenderingContext2D;
  near: CanvasRenderingContext2D;
}

export interface HeroScene {
  /** A still scene paints once per resize and never animates. */
  still?: boolean;
  /** Frames per second; slow scenes paint less often. Defaults to 30. */
  fps?: number;
  /** Paints one frame at `time` seconds. Both layers are cleared and scaled to
   *  CSS pixels before each call. */
  draw: (layers: HeroLayers, width: number, height: number, time: number) => void;
}

// Lavender and violet over the banner's deep-space purple.
const LAVENDER = "rgb(196, 181, 253)";
const VIOLET = "rgb(167, 139, 250)";
const ORCHID = "rgb(216, 180, 254)";
const INDIGO = "rgb(129, 140, 248)";
const WHITE = "rgb(255, 255, 255)";

// Sizes are tuned on a 260px-tall banner and scale with the canvas height, so
// the Appearance tab's small previews read as the same scene.
const REFERENCE_HEIGHT = 260;

const TAU = Math.PI * 2;
// Frequencies are scaled by irrational ratios so their sum never repeats.
const PHI = 1.618033988749895;
const SQRT2 = Math.SQRT2;

const fract = (v: number) => v - Math.floor(v);

/** A stable pseudo-random value in [0, 1) for an integer. */
const hash01 = (n: number) => fract(Math.sin(n * 12.9898 + 78.233) * 43758.5453);

const smoothstep = (edge0: number, edge1: number, v: number) => {
  const t = Math.min(1, Math.max(0, (v - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};

type Point2 = { x: number; y: number };

/** Adds an open polyline to the current path. */
const strokePolyline = (ctx: CanvasRenderingContext2D, points: Point2[]) => {
  ctx.moveTo(points[0].x, points[0].y);
  for (let k = 1; k < points.length; k++) {
    ctx.lineTo(points[k].x, points[k].y);
  }
};

const dot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number) => {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
};

/** Dust drifting up through the banner: soft discs out of focus behind the
 *  scene, and fine specks in front of it. */
const drawMotes = (
  { far, near }: HeroLayers,
  width: number,
  height: number,
  time: number,
  count: number
) => {
  const scale = height / REFERENCE_HEIGHT;
  for (let i = 0; i < count; i++) {
    const isFar = hash01(i * 13 + 5) < 0.45;
    const ctx = isFar ? far : near;
    // Far motes rise slower, for parallax.
    const speed = (0.002 + hash01(i * 3 + 1) * 0.005) * (isFar ? 0.5 : 1);
    const x = fract(hash01(i) + Math.sin(time * 0.025 + i) * 0.01) * width;
    const y = (1 - fract(hash01(i * 7 + 2) + time * speed)) * height;
    const twinkle = 0.5 + 0.5 * Math.sin(time * (0.2 + hash01(i * 5) * 0.3) + i);
    ctx.fillStyle = isFar ? VIOLET : WHITE;
    ctx.globalAlpha = isFar ? 0.08 + 0.1 * twinkle : 0.05 + 0.15 * twinkle;
    dot(ctx, x, y, (isFar ? 2 + hash01(i * 11) * 3 : 0.6 + hash01(i * 11) * 0.9) * scale);
    ctx.globalAlpha = 1;
  }
};

// ---------------------------------------------------------------------------
// Double helix

interface HelixStrand {
  /** Axis height as a fraction of the canvas. */
  centerY: number;
  /** Strand radius as a fraction of the canvas height. */
  radius: number;
  /** Length of one full turn, in canvas heights. */
  pitch: number;
  /** Axis slope across the canvas. */
  tilt: number;
  /** Spin, in radians per second. */
  spin: number;
  /** Travel along the axis, in canvas heights per second. */
  drift: number;
  opacity: number;
  seed: number;
}

/** Where a strand paints: the half turned away from the viewer can sit on the
 *  blurred plane, so focus falls off around the helix. */
interface HelixPlanes {
  back: CanvasRenderingContext2D;
  front: CanvasRenderingContext2D;
}

// One base colour per nucleotide, in the order A, T, G, C; partners share a
// rung.
const BASE_COLORS = [LAVENDER, VIOLET, ORCHID, INDIGO];
const COMPLEMENT = [1, 0, 3, 2];
// A and G are two-ringed purines, T and C single-ringed pyrimidines, so each
// rung's join sits off centre, toward the pyrimidine.
const PURINE_SHARE = 0.6;
const isPurine = (base: number) => base % 2 === 0;
// B-DNA has ten base pairs per turn and an offset between its strands that
// leaves a major and a minor groove.
const BASE_PAIRS_PER_TURN = 10;
const GROOVE_OFFSET = 2.4;
// Both fine enough that neither neighbouring ribbon segments nor one segment
// turning through the crossfade between planes visibly steps in brightness:
// side-on, where depth changes fastest, each segment differs from the next by
// a few percent.
const DEPTH_BUCKETS = 128;
const BACKBONE_SAMPLES = 24;

type HelixPoint = { x: number; y: number; z: number };

const drawHelix = (
  { back: backPlane, front: frontPlane }: HelixPlanes,
  width: number,
  height: number,
  time: number,
  strand: HelixStrand
) => {
  // Widths grow with the strand, so a close strand reads as close.
  const scale = (height / REFERENCE_HEIGHT) * (strand.radius / 0.2);
  const pitch = strand.pitch * height;
  const step = pitch / BASE_PAIRS_PER_TURN;
  const pad = step * 2;
  const offset = time * strand.drift * height;
  const firstIndex = Math.floor((-pad - offset) / step);
  const lastIndex = Math.ceil((width + pad - offset) / step);

  // The axis wanders and the radius breathes on slow, unrelated periods, so
  // the strand never settles into a visible cycle.
  const axisY = (x: number) =>
    height * strand.centerY +
    strand.tilt * (x - width / 2) +
    height * 0.05 * Math.sin((x / width) * 1.6 + time * 0.022 + strand.seed) +
    height * 0.025 * Math.sin((x / width) * 3.4 * SQRT2 - time * 0.035 * PHI + strand.seed);
  const radiusAt = (x: number) =>
    strand.radius * height * (1 + 0.1 * Math.sin(time * 0.03 + (x / width) * 2 + strand.seed));

  // `i` counts base pairs and may be fractional between them.
  const strandPoint = (i: number, groove: number): HelixPoint => {
    const x = i * step + offset;
    const angle = (i / BASE_PAIRS_PER_TURN) * TAU + time * strand.spin + groove;
    return { x, y: axisY(x) + radiusAt(x) * Math.sin(angle), z: Math.cos(angle) };
  };
  const depth = (z: number) => (z + 1) / 2;
  const bucketOf = (z: number) => Math.min(DEPTH_BUCKETS - 1, Math.floor(depth(z) * DEPTH_BUCKETS));
  // How much of a point at depth `z` sits on the sharp plane; the two planes
  // crossfade where the strand turns side-on, so it never visibly cuts over.
  const sharpShare = (z: number) =>
    backPlane === frontPlane ? (z >= 0 ? 1 : 0) : smoothstep(-0.3, 0.3, z);
  const planeShare = (z: number, front: boolean) => (front ? sharpShare(z) : 1 - sharpShare(z));

  const backbones = [0, GROOVE_OFFSET].map((groove) => {
    const points: HelixPoint[] = [];
    for (let i = firstIndex; i <= lastIndex; i += 1 / BACKBONE_SAMPLES) {
      points.push(strandPoint(i, groove));
    }
    return points;
  });

  // Backbones are ribbons, wide where they face the viewer and narrowing as
  // they turn away. Segments are grouped by depth, and each bucket is one
  // filled path.
  const halfWidth = (z: number) => (1 + 3.6 * depth(z)) * scale;
  const normalAt = (points: HelixPoint[], i: number) => {
    const a = points[Math.max(0, i - 1)];
    const c = points[Math.min(points.length - 1, i + 1)];
    const len = Math.hypot(c.x - a.x, c.y - a.y) || 1;
    return { x: -(c.y - a.y) / len, y: (c.x - a.x) / len };
  };
  const buckets: { a: HelixPoint; c: HelixPoint; na: Point2; nc: Point2 }[][] = Array.from(
    { length: DEPTH_BUCKETS },
    () => []
  );
  for (const points of backbones) {
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1];
      const c = points[i];
      buckets[bucketOf((a.z + c.z) / 2)].push({
        a,
        c,
        na: normalAt(points, i - 1),
        nc: normalAt(points, i),
      });
    }
  }
  const drawRibbons = (front: boolean) => {
    const ctx = front ? frontPlane : backPlane;
    for (let b = 0; b < DEPTH_BUCKETS; b++) {
      const segments = buckets[b];
      if (segments.length === 0) {
        continue;
      }
      const lit = (b + 0.5) / DEPTH_BUCKETS;
      const share = planeShare(lit * 2 - 1, front);
      if (share < 0.01) {
        continue;
      }
      ctx.fillStyle = LAVENDER;
      ctx.globalAlpha = strand.opacity * (0.08 + 0.4 * lit) * share;
      ctx.beginPath();
      for (const { a, c, na, nc } of segments) {
        const wa = halfWidth(a.z);
        const wc = halfWidth(c.z);
        ctx.moveTo(a.x + na.x * wa, a.y + na.y * wa);
        ctx.lineTo(c.x + nc.x * wc, c.y + nc.y * wc);
        ctx.lineTo(c.x - nc.x * wc, c.y - nc.y * wc);
        ctx.lineTo(a.x - na.x * wa, a.y - na.y * wa);
        ctx.closePath();
      }
      ctx.fill();
      if (lit <= 0.5) {
        continue;
      }
      // A sheen along the upper edge of the facing ribbon, lit from above.
      ctx.strokeStyle = WHITE;
      ctx.lineWidth = 0.7 * scale;
      ctx.globalAlpha = strand.opacity * 0.4 * (lit - 0.5) * 2 * share;
      ctx.beginPath();
      for (const { a, c, na, nc } of segments) {
        const sa = (na.y > 0 ? -1 : 1) * halfWidth(a.z) * 0.45;
        const sc = (nc.y > 0 ? -1 : 1) * halfWidth(c.z) * 0.45;
        ctx.moveTo(a.x + na.x * sa, a.y + na.y * sa);
        ctx.lineTo(c.x + nc.x * sc, c.y + nc.y * sc);
      }
      ctx.stroke();
    }
  };

  // Base pairs: flat slabs, each base its own colour, with a gap where the
  // hydrogen bonds pair them. Each pair brightens now and then.
  const drawBases = (front: boolean) => {
    const ctx = front ? frontPlane : backPlane;
    ctx.lineCap = "butt";
    for (let i = firstIndex; i <= lastIndex; i++) {
      const a = strandPoint(i, 0);
      const b = strandPoint(i, GROOVE_OFFSET);
      const z = (a.z + b.z) / 2;
      const share = planeShare(z, front);
      if (share < 0.01) {
        continue;
      }
      const key = i + strand.seed * 1000;
      const base = Math.floor(hash01(key) * 4);
      const glow =
        Math.max(0, Math.sin(time * (0.08 + 0.1 * hash01(key + 7)) + hash01(key + 3) * TAU)) ** 8;
      const span = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const ux = (b.x - a.x) / span;
      const uy = (b.y - a.y) / span;
      const inset = Math.min(span * 0.08, halfWidth(a.z) * 0.6);
      const gap = Math.min(span * 0.05, 1.6 * scale);
      const split = isPurine(base) ? PURINE_SHARE : 1 - PURINE_SHARE;
      const midX = a.x + (b.x - a.x) * split;
      const midY = a.y + (b.y - a.y) * split;
      ctx.lineWidth = (1.4 + 2.6 * depth(z)) * scale;
      ctx.globalAlpha = strand.opacity * (0.12 + 0.36 * depth(z)) * (1 + 1.2 * glow) * share;
      ctx.strokeStyle = BASE_COLORS[base];
      ctx.beginPath();
      ctx.moveTo(a.x + ux * inset, a.y + uy * inset);
      ctx.lineTo(midX - ux * gap, midY - uy * gap);
      ctx.stroke();
      ctx.strokeStyle = BASE_COLORS[COMPLEMENT[base]];
      ctx.beginPath();
      ctx.moveTo(midX + ux * gap, midY + uy * gap);
      ctx.lineTo(b.x - ux * inset, b.y - uy * inset);
      ctx.stroke();
    }
  };

  drawRibbons(false);
  drawBases(false);
  drawBases(true);
  drawRibbons(true);
  backPlane.globalAlpha = 1;
  frontPlane.globalAlpha = 1;
};

const helixScene = (): HeroScene => ({
  draw: (layers, width, height, time) => {
    drawMotes(layers, width, height, time, Math.round(width / 30));
    // Seen up close: a distant strand out of focus behind, turning the other way.
    drawHelix({ back: layers.far, front: layers.far }, width, height, time, {
      centerY: 0.26,
      radius: 0.12,
      pitch: 1.5,
      tilt: 0.04,
      spin: -0.08,
      drift: -0.008,
      opacity: 0.7,
      seed: 7,
    });
    // The near strand's far side falls out of focus.
    drawHelix({ back: layers.far, front: layers.near }, width, height, time, {
      centerY: 0.6,
      radius: 0.3,
      pitch: 2.7,
      tilt: -0.05,
      spin: 0.13,
      drift: 0.01,
      opacity: 1,
      seed: 0,
    });
  },
});

// ---------------------------------------------------------------------------
// Cells

/** Traces a nucleus: an ellipse along `rotation` with a slightly uneven rim. */
const traceNucleus = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rotation: number,
  seed: number
) => {
  for (let k = 0; k <= 36; k++) {
    const a = (k / 36) * TAU;
    const wobble = 1 + 0.05 * Math.sin(2 * a + seed) + 0.03 * Math.sin(5 * a + seed * 3);
    const x = rx * wobble * Math.cos(a);
    const y = ry * wobble * Math.sin(a);
    const px = cx + x * Math.cos(rotation) - y * Math.sin(rotation);
    const py = cy + x * Math.sin(rotation) + y * Math.cos(rotation);
    if (k === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.closePath();
};

/** Condensed chromosomes as short rods, scattered along a line across `angle`. */
const drawChromosomes = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  spanPx: number,
  scale: number,
  seed: number
) => {
  ctx.beginPath();
  for (let i = 0; i < 9; i++) {
    const along = (hash01(seed * 7 + i) - 0.5) * spanPx;
    const across = (hash01(seed * 11 + i) - 0.5) * spanPx * 0.25;
    const tilt = angle + (hash01(seed * 13 + i) - 0.5) * 1.2;
    const x = cx + Math.cos(angle + Math.PI / 2) * along + Math.cos(angle) * across;
    const y = cy + Math.sin(angle + Math.PI / 2) * along + Math.sin(angle) * across;
    const half = 1.6 * scale;
    ctx.moveTo(x - Math.cos(tilt) * half, y - Math.sin(tilt) * half);
    ctx.lineTo(x + Math.cos(tilt) * half, y + Math.sin(tilt) * half);
  }
  ctx.stroke();
};

/**
 * Cells as a confocal section through an epithelium. The cells pack edge to
 * edge, so they tile the sheet rather than pile up: each is the Voronoi region
 * of a seed on a jittered hex lattice. Membranes are stained and inset a little
 * from their neighbours', so a junction reads as a fine double line, with a
 * bright point where three cells meet. Nuclei carry a DNA stain. The sheet is
 * gently wavy, so the focal plane cuts it in drifting patches, and it migrates
 * slowly across the banner, a few cells rounding up and dividing on the way.
 */

const CELL_SPACING = 68;
const CELL_SPEED = 2.2;
// Lattice rows and columns beyond each edge, so the cut-off cells there are
// replaced out of sight.
const CELL_MARGIN = 3;
const CELL_ROUNDING_S = 14;
const CELL_PART_S = 9;
const CELL_SETTLE_S = 20;
const CELL_ROW = Math.sqrt(3) / 2;
// Seeds further apart than this never share an edge.
const CELL_NEIGHBOUR_REACH = 3;

interface CellSeed {
  x: number;
  y: number;
  /** Both daughters of a dividing cell share the parent's id. */
  cell: number;
  /** A stable value for this cell's look. */
  key: number;
  /** -1 or 1 for a daughter, 0 for an undivided cell. */
  daughter: number;
  /** 0 is interphase, 1 is rounded up with chromosomes condensed. */
  mitosis: number;
  /** Cytokinesis: 0 is one cell, 1 is two daughters with a junction between. */
  parting: number;
  /** How far the cell has rounded up from the sheet; daughters settle back. */
  rounded: number;
  /** The division axis, in radians. */
  axis: number;
}

/** Every seed of the sheet at `time`. Each lattice row slides right and wraps
 *  around out of sight, coming back as new cells. */
const cellSeeds = (width: number, height: number, time: number): CellSeed[] => {
  const scale = height / REFERENCE_HEIGHT;
  const spacing = CELL_SPACING * scale;
  const margin = CELL_MARGIN * spacing;
  const cols = Math.ceil((width + 2 * margin) / spacing);
  const span = cols * spacing;
  const rows = Math.ceil((height + 2 * margin) / (spacing * CELL_ROW));
  const speed = CELL_SPEED * scale;
  const seeds: CellSeed[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const travelled = c * spacing + (r % 2) * spacing * 0.5 + time * speed;
      const lap = Math.floor(travelled / span);
      const along = travelled - lap * span;
      const cell = r * cols + c;
      const key = cell * 131 + lap * 7919;
      // A missing seed lets its neighbours spread, so cell sizes vary.
      if (hash01(key + 9) < 0.14) {
        continue;
      }
      const x =
        along -
        margin +
        (hash01(key + 1) - 0.5) * 0.5 * spacing +
        0.05 * spacing * Math.sin(time * 0.09 + key);
      const y =
        r * spacing * CELL_ROW -
        margin +
        (hash01(key + 2) - 0.5) * 0.5 * spacing +
        0.05 * spacing * Math.sin(time * 0.07 * PHI + key * 2);
      // A dividing cell starts somewhere in the visible stretch of its trip.
      const start = (margin + (0.05 + 0.6 * hash01(key + 4)) * width) / speed;
      const age = along / speed;
      const divides = hash01(key + 3) < 0.1;
      const mitosis = divides ? smoothstep(start, start + CELL_ROUNDING_S, age) : 0;
      const parted = start + CELL_ROUNDING_S + CELL_PART_S;
      const parting = divides ? smoothstep(start + CELL_ROUNDING_S, parted, age) : 0;
      const rounded =
        mitosis * (1 - 0.5 * parting) * (1 - smoothstep(parted, parted + CELL_SETTLE_S, age));
      const axis = hash01(key + 5) * Math.PI;
      const base = { cell, key, mitosis, parting, rounded, axis };
      if (parting > 0) {
        const offset = 0.24 * spacing * parting;
        for (const daughter of [-1, 1]) {
          seeds.push({
            ...base,
            x: x + Math.cos(axis) * offset * daughter,
            y: y + Math.sin(axis) * offset * daughter,
            daughter,
          });
        }
      } else {
        seeds.push({ ...base, x, y, daughter: 0 });
      }
    }
  }
  return seeds;
};

interface CellPolygon {
  points: Point2[];
  /** The seed across each edge `points[i]` to `points[i + 1]`, or -1 at the
   *  sheet's bounds. */
  across: number[];
}

/** Keeps the part of `polygon` where `nx * x + ny * y <= d`, labelling the new
 *  edge along the cut with `label`. */
const clipPolygon = (
  { points, across }: CellPolygon,
  nx: number,
  ny: number,
  d: number,
  label: number
): CellPolygon => {
  const out: CellPolygon = { points: [], across: [] };
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const pd = nx * p.x + ny * p.y - d;
    const qd = nx * q.x + ny * q.y - d;
    if (pd <= 0) {
      out.points.push(p);
      out.across.push(across[i]);
    }
    if (pd <= 0 !== qd <= 0) {
      const t = pd / (pd - qd);
      out.points.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t });
      out.across.push(pd <= 0 ? label : across[i]);
    }
  }
  return out;
};

/** The Voronoi region of seed `i`, cut down from the sheet's bounds by each
 *  nearby seed's half-plane. */
const voronoiCell = (
  seeds: CellSeed[],
  i: number,
  reach: number,
  bounds: { left: number; top: number; right: number; bottom: number }
): CellPolygon => {
  const s = seeds[i];
  let polygon: CellPolygon = {
    points: [
      { x: bounds.left, y: bounds.top },
      { x: bounds.right, y: bounds.top },
      { x: bounds.right, y: bounds.bottom },
      { x: bounds.left, y: bounds.bottom },
    ],
    across: [-1, -1, -1, -1],
  };
  for (let j = 0; j < seeds.length && polygon.points.length > 0; j++) {
    const o = seeds[j];
    const nx = o.x - s.x;
    const ny = o.y - s.y;
    if (j === i || Math.abs(nx) > reach || Math.abs(ny) > reach) {
      continue;
    }
    const d = (o.x * o.x + o.y * o.y - s.x * s.x - s.y * s.y) / 2;
    polygon = clipPolygon(polygon, nx, ny, d, j);
  }
  return polygon;
};

/** The area centroid. Unlike the mean of the corners, it moves smoothly when
 *  neighbours trade places and a corner appears or vanishes. */
const polygonCentroid = (points: Point2[]): Point2 => {
  let area = 0;
  let x = 0;
  let y = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const cross = p.x * q.y - q.x * p.y;
    area += cross;
    x += (p.x + q.x) * cross;
    y += (p.y + q.y) * cross;
  }
  return { x: x / (3 * area), y: y / (3 * area) };
};

/** How far the wavy sheet sits from the focal plane at a point, as a sharpness
 *  from 0 to 1. The waves ride with the sheet; the focal plane breathes. */
const cellSharpness = (x: number, y: number, time: number, scale: number) => {
  const tx = (x - time * CELL_SPEED * scale) / scale;
  const ty = y / scale;
  const sheet =
    0.5 +
    0.26 * Math.sin(tx * 0.0062 + 0.9 * Math.sin(ty * 0.011 + 0.4)) +
    0.16 * Math.sin(ty * 0.017 - tx * 0.0031 + 1.3);
  const focus =
    0.55 + 0.1 * Math.sin((time * TAU) / 95) + 0.04 * Math.sin((time * TAU) / (95 * PHI));
  return 1 - smoothstep(0.08, 0.3, Math.abs(sheet - focus));
};

/** How strongly the sheet is stained at a point, from 0 to 1: the stain is
 *  patchy, so the sheet fades out in places rather than tiling the banner. */
const cellStain = (x: number, y: number, time: number, scale: number) => {
  const tx = (x - time * CELL_SPEED * scale) / scale;
  const ty = y / scale;
  const field =
    0.5 +
    0.3 * Math.sin(tx * 0.0041 + 1.7 + 0.8 * Math.sin(ty * 0.009)) +
    0.2 * Math.sin(tx * 0.0093 - ty * 0.006 + 0.5);
  return 0.15 + 0.85 * smoothstep(0.25, 0.75, field);
};

/** Traces a polygon with rounded corners: each corner is cut back by `round`
 *  of its edges and bridged with a curve. Edges for which `skip` is true are
 *  left open. */
const traceRounded = (
  ctx: CanvasRenderingContext2D,
  points: Point2[],
  round: number,
  skip?: (edge: number) => boolean
) => {
  const n = points.length;
  const lerp = (a: Point2, b: Point2, t: number) => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
  });
  const leave = (i: number) => lerp(points[i], points[(i + 1) % n], round);
  ctx.moveTo(leave(n - 1).x, leave(n - 1).y);
  for (let i = 0; i < n; i++) {
    const v = points[i];
    const a = lerp(v, points[(i + n - 1) % n], round);
    const b = leave(i);
    // The edge arriving at this corner.
    if (skip?.((i + n - 1) % n)) {
      ctx.moveTo(a.x, a.y);
    } else {
      ctx.lineTo(a.x, a.y);
    }
    for (const t of [0.25, 0.5, 0.75, 1]) {
      const u = 1 - t;
      ctx.lineTo(
        u * u * a.x + 2 * u * t * v.x + t * t * b.x,
        u * u * a.y + 2 * u * t * v.y + t * t * b.y
      );
    }
  }
};

const drawCell = (
  { far, near }: HeroLayers,
  seeds: CellSeed[],
  index: number,
  polygon: CellPolygon,
  time: number,
  scale: number
) => {
  const seed = seeds[index];
  const { points, across } = polygon;
  const n = points.length;
  if (n < 3) {
    return;
  }
  const centre = polygonCentroid(points);
  const { key, mitosis, parting, rounded, axis } = seed;
  // Each membrane sits back from the junction, and a rounding cell pulls away.
  const gap = (1.4 + 2.2 * rounded) * scale;
  const inset = points.map((p) => {
    const d = Math.hypot(p.x - centre.x, p.y - centre.y) || 1;
    const k = Math.max(0, 1 - gap / d);
    return { x: centre.x + (p.x - centre.x) * k, y: centre.y + (p.y - centre.y) * k };
  });
  const isSibling = (edge: number) => across[edge] >= 0 && seeds[across[edge]].cell === seed.cell;
  const round = 0.24 + 0.24 * rounded;
  const sharp = cellSharpness(centre.x, centre.y, time, scale);
  const soft = 1 - sharp;
  // Stain is uneven from cell to cell; a rounded-up cell is thicker, so brighter.
  const stain =
    (0.55 + 0.45 * hash01(key + 11)) *
    (1 + 1.2 * rounded) *
    cellStain(centre.x, centre.y, time, scale);

  const spacing = CELL_SPACING * scale;
  const nr = spacing * (0.19 + 0.05 * hash01(key + 12)) * (seed.daughter === 0 ? 1 : 0.82);
  const nucleus = {
    x: seed.x + (centre.x - seed.x) * 0.5 + Math.cos(time * 0.03 + key) * nr * 0.12,
    y: seed.y + (centre.y - seed.y) * 0.5 + Math.sin(time * 0.025 + key) * nr * 0.12,
  };
  const nucleusAxis = hash01(key + 13) * Math.PI + Math.sin(time * 0.02 + key) * 0.15;
  const nucleusFade = Math.max(1 - smoothstep(0.15, 0.45, mitosis), smoothstep(0.5, 1, parting));

  // The out-of-focus sheet, under the lens blur.
  if (soft > 0.01) {
    far.fillStyle = VIOLET;
    far.globalAlpha = soft * stain * 0.05;
    far.beginPath();
    traceRounded(far, inset, round);
    far.fill();
    far.strokeStyle = LAVENDER;
    far.lineWidth = 2.2 * scale;
    far.globalAlpha = soft * stain * 0.2;
    far.stroke();
    if (nucleusFade > 0.01) {
      far.fillStyle = INDIGO;
      far.globalAlpha = soft * stain * nucleusFade * 0.13;
      far.beginPath();
      traceNucleus(far, nucleus.x, nucleus.y, nr * 1.25, nr, nucleusAxis, key);
      far.fill();
    }
  }
  if (sharp <= 0.01) {
    return;
  }

  near.fillStyle = VIOLET;
  near.globalAlpha = sharp * stain * 0.035;
  near.beginPath();
  traceRounded(near, inset, round);
  near.fill();

  near.strokeStyle = LAVENDER;
  near.lineWidth = 0.9 * scale;
  near.lineJoin = "round";
  near.globalAlpha = sharp * stain * 0.5;
  near.beginPath();
  traceRounded(near, inset, round, isSibling);
  near.stroke();

  // A new junction closes across a dividing cell from its edges inward.
  if (parting > 0) {
    near.lineCap = "round";
    near.globalAlpha = sharp * stain * 0.5 * parting;
    near.beginPath();
    for (let e = 0; e < n; e++) {
      if (!isSibling(e)) {
        continue;
      }
      // Between the rounded corners at either end.
      const p = inset[e];
      const q = inset[(e + 1) % n];
      const a = { x: p.x + (q.x - p.x) * round, y: p.y + (q.y - p.y) * round };
      const b = { x: q.x + (p.x - q.x) * round, y: q.y + (p.y - q.y) * round };
      const reach = 0.5 * smoothstep(0, 0.8, parting);
      near.moveTo(a.x, a.y);
      near.lineTo(a.x + (b.x - a.x) * reach, a.y + (b.y - a.y) * reach);
      near.moveTo(b.x, b.y);
      near.lineTo(b.x + (a.x - b.x) * reach, b.y + (a.y - b.y) * reach);
    }
    near.stroke();
  }

  // Tricellular junctions: each of the three cells adds a third.
  near.fillStyle = WHITE;
  for (let v = 0; v < n; v++) {
    const before = (v + n - 1) % n;
    if (across[before] < 0 || across[v] < 0) {
      continue;
    }
    const weight = isSibling(before) || isSibling(v) ? parting : 1;
    near.globalAlpha = sharp * stain * 0.13 * weight;
    dot(near, points[v].x, points[v].y, 1.3 * scale);
  }

  if (nucleusFade > 0.01) {
    // The DNA stain: a soft body, denser toward the middle, with grainy
    // chromatin and nucleoli as darker voids.
    near.fillStyle = INDIGO;
    for (const [size, a] of [
      [1, 0.2],
      [0.7, 0.12],
    ]) {
      near.globalAlpha = sharp * stain * nucleusFade * a;
      near.beginPath();
      traceNucleus(near, nucleus.x, nucleus.y, nr * 1.25 * size, nr * size, nucleusAxis, key);
      near.fill();
    }
    near.fillStyle = LAVENDER;
    near.globalAlpha = sharp * stain * nucleusFade * 0.12;
    near.beginPath();
    const ux = Math.cos(nucleusAxis);
    const uy = Math.sin(nucleusAxis);
    for (let c = 0; c < 14; c++) {
      const a = hash01(key * 3 + c) * TAU;
      const d = Math.sqrt(hash01(key * 5 + c)) * 0.8;
      const lx = Math.cos(a) * nr * 1.25 * d;
      const ly = Math.sin(a) * nr * d;
      const x = nucleus.x + lx * ux - ly * uy;
      const y = nucleus.y + lx * uy + ly * ux;
      const r = (0.6 + 0.7 * hash01(key * 7 + c)) * scale;
      near.moveTo(x + r, y);
      near.arc(x, y, r, 0, TAU);
    }
    near.fill();
    near.globalCompositeOperation = "destination-out";
    near.globalAlpha = 0.4 * sharp * nucleusFade;
    for (let k = 0; k < 1 + Math.round(hash01(key + 14)); k++) {
      dot(
        near,
        nucleus.x + (hash01(key * 11 + k) - 0.5) * nr * 0.9,
        nucleus.y + (hash01(key * 13 + k) - 0.5) * nr * 0.6,
        nr * (0.16 + 0.06 * k)
      );
    }
    near.globalCompositeOperation = "lighter";
  }

  // Chromosomes gather on a plate across the division axis, then each
  // daughter carries half away.
  const condensed = smoothstep(0.2, 0.5, mitosis) * (1 - smoothstep(0.4, 1, parting));
  if (condensed > 0.01) {
    near.strokeStyle = ORCHID;
    near.lineCap = "round";
    near.lineWidth = 1.3 * scale;
    near.globalAlpha = sharp * condensed * 0.75;
    drawChromosomes(
      near,
      // The seed, not the centroid, which jumps when the cell splits in two.
      seed.x,
      seed.y,
      axis,
      spacing * (seed.daughter === 0 ? 0.5 : 0.36),
      scale,
      key + seed.daughter
    );
  }
};

const cellsScene = (): HeroScene => ({
  fps: 15,
  draw: (layers, width, height, time) => {
    const scale = height / REFERENCE_HEIGHT;
    const spacing = CELL_SPACING * scale;
    const margin = CELL_MARGIN * spacing;
    drawMotes(layers, width, height, time, Math.round(width / 60));
    const seeds = cellSeeds(width, height, time);
    const bounds = { left: -margin, top: -margin, right: width + margin, bottom: height + margin };
    const reach = CELL_NEIGHBOUR_REACH * spacing;
    // Stains are emitted light, so overlapping blur adds up.
    layers.far.globalCompositeOperation = "lighter";
    layers.near.globalCompositeOperation = "lighter";
    for (let i = 0; i < seeds.length; i++) {
      const { x, y } = seeds[i];
      if (x < -spacing || x > width + spacing || y < -spacing || y > height + spacing) {
        continue;
      }
      drawCell(layers, seeds, i, voronoiCell(seeds, i, reach, bounds), time, scale);
    }
    for (const ctx of [layers.far, layers.near]) {
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 1;
    }
  },
});

// ---------------------------------------------------------------------------
// Signals

const RIDGE_FEATURES = 12;
const RIDGE_GLINTS = 2;
// Ridges at or beyond the blur depth sit behind the lens blur, those nearer
// than the focus depth are sharp, and the ones between crossfade, so focus
// falls off over a couple of ridges rather than at one.
const SIGNAL_BLUR_DEPTH = 0.2;
const SIGNAL_FOCUS_DEPTH = 0.45;

/**
 * A ridgeline plot seen in perspective. Each feature is a swell that spans
 * several neighbouring ridges, so a signal rises through the stack rather than
 * along one line. Nearer ridges hide the ones behind them, and features grow,
 * wander and subside on unrelated periods.
 */
const signalsScene = (): HeroScene => ({
  draw: (layers, width, height, time) => {
    const scale = height / REFERENCE_HEIGHT;
    const ridges = Math.max(6, Math.round(height / 24));
    const step = Math.max(3, width / 320);
    const pad = width * 0.12;
    const peak = height * 0.4;

    // `u` is nearness: 0 is the horizon, 1 the bottom edge.
    const nearness = (ridge: number) => ridge / (ridges - 1);
    const baseline = (u: number) => height * (0.2 + 0.86 * u ** 1.25);
    // Perspective: a ridge's world-space x maps narrower toward the horizon.
    const toScreenX = (x: number, u: number) => width / 2 + (x - width / 2) * (0.86 + 0.24 * u);

    const features = Array.from({ length: RIDGE_FEATURES }, (_, f) => {
      const period = 27 + hash01(f * 7 + 1) * 24;
      const local = time + hash01(f * 13 + 2) * period;
      const cycle = Math.floor(local / period);
      const age = local - cycle * period;
      const key = f * 97 + cycle * 7919;
      return {
        // Swells keep to the outer thirds, framing the text rather than hiding behind it.
        x:
          (hash01(key + 1) < 0.5 ? 0.04 + hash01(key + 8) * 0.3 : 0.66 + hash01(key + 8) * 0.3) *
            width +
          (hash01(key + 2) - 0.5) * 9 * scale * age,
        ridge: hash01(key + 3) * (ridges - 1),
        widthPx: (0.03 + hash01(key + 4) * 0.045) * width,
        span: 0.9 + hash01(key + 5) * 1.1,
        // Grows in, holds, and subsides over its cycle.
        amplitude:
          (0.55 + 0.45 * hash01(key + 6)) *
          smoothstep(0, period * 0.3, age) *
          (1 - smoothstep(period * 0.7, period, age)),
      };
    });

    /** Heights along one ridge, skipping swells too far off to lift it. */
    const ridgeProfile = (ridge: number) => {
      const u = nearness(ridge);
      const swells = features.flatMap((f) => {
        const amplitude = f.amplitude * Math.exp(-(((ridge - f.ridge) / f.span) ** 2));
        return amplitude > 1e-6 ? [{ x: f.x, widthPx: f.widthPx, amplitude }] : [];
      });
      // A soft cap: small swells keep their height, and the tallest, where
      // features overlap on near ridges, come down by about half.
      const cap = height * 0.32 * (0.55 + 0.45 * u);
      const gain = (peak * (0.45 + 0.75 * u)) / cap;
      return (x: number) => {
        let h =
          0.02 * (1 + Math.sin(x / (width * 0.07) + time * 0.07 + ridge * 0.9)) +
          0.015 * (1 + Math.sin(x / (width * 0.031) - time * 0.05 * PHI + ridge * 1.7));
        for (const f of swells) {
          const d = (x - f.x) / f.widthPx;
          if (d * d < 25) {
            h += f.amplitude * Math.exp(-d * d);
          }
        }
        return cap * Math.tanh(h * gain);
      };
    };

    const traced = Array.from({ length: ridges }, (_, ridge) => {
      const u = nearness(ridge);
      const base = baseline(u);
      const heightAt = ridgeProfile(ridge);
      const points: Point2[] = [];
      for (let x = -pad; x <= width + pad; x += step) {
        points.push({ x: toScreenX(x, u), y: base - heightAt(x) });
      }
      return points;
    });

    for (let ridge = 0; ridge < ridges; ridge++) {
      const u = nearness(ridge);
      const points = traced[ridge];
      const sharp = Math.min(
        1,
        Math.max(0, (u - SIGNAL_BLUR_DEPTH) / (SIGNAL_FOCUS_DEPTH - SIGNAL_BLUR_DEPTH))
      );
      const planes: [CanvasRenderingContext2D, number][] = [
        [layers.far, 1 - sharp],
        [layers.near, sharp],
      ];
      const tracePath = (target: CanvasRenderingContext2D) => {
        target.beginPath();
        strokePolyline(target, points);
        target.lineTo(points[points.length - 1].x, height + 2);
        target.lineTo(points[0].x, height + 2);
        target.closePath();
      };
      // Each ridge hides what lies behind it, on both planes, so the
      // backdrop's light shows through instead of a flat fill.
      for (const target of sharp > 0 ? [layers.far, layers.near] : [layers.far]) {
        target.globalCompositeOperation = "destination-out";
        tracePath(target);
        target.fill();
        target.globalCompositeOperation = "source-over";
      }
      // A faint body under the ridge, then its line.
      for (const [ctx, share] of planes) {
        if (share <= 0) {
          continue;
        }
        ctx.fillStyle = VIOLET;
        ctx.globalAlpha = (0.03 + 0.04 * u) * share;
        tracePath(ctx);
        ctx.fill();
        ctx.strokeStyle = ridge % 3 === 0 ? ORCHID : LAVENDER;
        ctx.lineWidth = (0.8 + 1.1 * u) * scale;
        ctx.lineJoin = "round";
        ctx.globalAlpha =
          (0.22 + 0.5 * Math.sin(Math.PI * Math.min(1, u * 1.15) ** 0.9) ** 0.6) * share;
        ctx.beginPath();
        strokePolyline(ctx, points);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // Glints drift slowly along near ridges, breathing as they go.
    const { near } = layers;
    // Only on fully sharp ridges, where a crisp glint belongs.
    const firstGlintRidge = Math.ceil(SIGNAL_FOCUS_DEPTH * (ridges - 1));
    for (let g = 0; g < RIDGE_GLINTS; g++) {
      const speed = 0.02 + hash01(g + 40) * 0.014;
      const progress = time * speed + hash01(g + 90);
      const run = Math.floor(progress);
      const ridge =
        firstGlintRidge + Math.floor(hash01(run * 31 + g * 7) * (ridges - firstGlintRidge));
      const u = nearness(ridge);
      const x = fract(progress) * (width + pad * 2) - pad;
      const sx = toScreenX(x, u);
      const sy = baseline(u) - ridgeProfile(ridge)(x);
      const breath = 0.5 + 0.5 * Math.sin(time * 0.8 + g * 2);
      // Fade at the ends of each run so glints never pop.
      const edge = Math.sin(Math.PI * fract(progress));
      near.fillStyle = VIOLET;
      near.globalAlpha = (0.12 + 0.12 * breath) * edge;
      dot(near, sx, sy, (7 + 4 * breath) * scale);
      near.fillStyle = WHITE;
      near.globalAlpha = (0.55 + 0.3 * breath) * edge;
      dot(near, sx, sy, 1.8 * scale);
    }
    near.globalAlpha = 1;
    // After the ridges, which would otherwise erase them.
    drawMotes(layers, width, height, time, Math.round(width / 40));
  },
});

// ---------------------------------------------------------------------------
// Still

// A moment of the signals scene with a swell to each side and a quiet middle
// behind the greeting.
const STILL_TIME = 111;

/** The motionless banner: one fixed frame of the signals scene. It ignores
 *  time. */
const stillScene = (): HeroScene => {
  const signals = signalsScene();
  return {
    still: true,
    draw: (layers, width, height) => signals.draw(layers, width, height, STILL_TIME),
  };
};

// ---------------------------------------------------------------------------
// Neurons

/** Seeded PRNG (mulberry32), so a scene's layout is fixed for its lifetime. */
const seededRandom = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

interface NeuriteBranch {
  /** Index of the branch this one grows from, or -1 for the cell body. */
  parent: number;
  /** Heading relative to the parent's end, or absolute from the cell body. */
  angle: number;
  /** Length in reference pixels. */
  length: number;
  /** Total turn along the branch, in radians. */
  curvature: number;
  level: number;
}

interface Neuron {
  /** Cell body position, as canvas fractions. */
  x: number;
  y: number;
  /** Size and brightness; 1 is nearest. */
  depth: number;
  /** Primary dendrite angles are relative to the axon's heading. */
  dendrites: NeuriteBranch[];
  /** The neuron the axon reaches toward, so spikes seem to pass along. */
  target: number;
  axonCurvature: number;
  axonJitter: number;
  terminals: NeuriteBranch[];
  /** Mean seconds between firings. */
  interval: number;
  seed: number;
}

const SOMA_POINTS = 48;

/** Traces a cell body as a closed subpath: a circle whose rim wobbles on
 *  unrelated periods. */
const traceSoma = (
  ctx: CanvasRenderingContext2D,
  soma: { x: number; y: number; r: number },
  time: number,
  seed: number
) => {
  for (let k = 0; k <= SOMA_POINTS; k++) {
    const a = (k / SOMA_POINTS) * TAU;
    const r =
      soma.r *
      (1 +
        0.055 * Math.sin(3 * a + time * 0.31 + seed) +
        0.035 * Math.sin(5 * a - time * 0.23 * PHI + seed * 2) +
        0.025 * Math.sin(2 * a + time * 0.17 * SQRT2 + seed * 3));
    const x = soma.x + r * Math.cos(a);
    const y = soma.y + r * Math.sin(a);
    if (k === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
};

const NEURITE_STEPS = 5;
const SPIKE_SPEED = 42;

/** Grows a dendritic tree: primary branches that fork twice. */
const growDendrites = (random: () => number): NeuriteBranch[] => {
  const branches: NeuriteBranch[] = [];
  const primaries = 4 + Math.floor(random() * 3);
  for (let i = 0; i < primaries; i++) {
    // Spread around the cell body, leaving room for the axon.
    const angle = 0.9 + ((i + random() * 0.6) / primaries) * (TAU - 1.8);
    branches.push({
      parent: -1,
      angle,
      length: 22 + random() * 22,
      curvature: (random() - 0.5) * 0.8,
      level: 0,
    });
  }
  for (let level = 1; level <= 2; level++) {
    const parents = branches.flatMap((b, index) => (b.level === level - 1 ? [index] : []));
    for (const parent of parents) {
      for (const side of [-1, 1]) {
        if (level === 2 && random() < 0.3) {
          continue;
        }
        branches.push({
          parent,
          angle: side * (0.3 + random() * 0.45),
          length: branches[parent].length * (0.55 + random() * 0.25),
          curvature: (random() - 0.5) * 0.9,
          level,
        });
      }
    }
  }
  return branches;
};

/** The spray of terminals at an axon's end. */
const growTerminals = (random: () => number): NeuriteBranch[] =>
  Array.from({ length: 3 }, (_, i) => ({
    parent: 0,
    angle: (i - 1) * 0.5 + (random() - 0.5) * 0.3,
    length: 10 + random() * 10,
    curvature: (random() - 0.5) * 0.6,
    level: 1,
  }));

/** Lays out branches at `time`, with a gentle sway. Parents precede children;
 *  `baseAngle` turns the branches that leave the cell body. */
const traceBranches = (
  branches: NeuriteBranch[],
  origin: Point2,
  originRadius: number,
  baseAngle: number,
  scale: number,
  time: number,
  seed: number
) => {
  const traced: { points: Point2[]; heading: number }[] = [];
  for (const [index, branch] of branches.entries()) {
    const sway =
      0.05 * (branch.level + 1) * Math.sin(time * 0.09 + seed + index * 1.3) +
      0.03 * Math.sin(time * 0.055 * PHI + index);
    let heading: number;
    let start: Point2;
    if (branch.parent === -1) {
      heading = baseAngle + branch.angle + sway * 0.5;
      start = {
        x: origin.x + Math.cos(heading) * originRadius,
        y: origin.y + Math.sin(heading) * originRadius,
      };
    } else {
      const parent = traced[branch.parent];
      heading = parent.heading + branch.angle + sway;
      start = parent.points[parent.points.length - 1];
    }
    const points = [start];
    const stepLength = (branch.length * scale) / NEURITE_STEPS;
    for (let k = 0; k < NEURITE_STEPS; k++) {
      heading += branch.curvature / NEURITE_STEPS;
      const last = points[points.length - 1];
      points.push({
        x: last.x + Math.cos(heading) * stepLength,
        y: last.y + Math.sin(heading) * stepLength,
      });
    }
    traced.push({ points, heading });
  }
  return traced;
};

/** Seconds since the neuron last fired, or undefined if it is between spikes. */
const sinceFiring = (neuron: Neuron, index: number, time: number) => {
  const local = time / neuron.interval + hash01(index + 3);
  const beat = Math.floor(local);
  // Some beats are skipped and the rest land at a random point in their
  // window, so firing is irregular.
  if (hash01(beat * 17 + index * 101) < 0.3) {
    return undefined;
  }
  const fireAt = hash01(beat * 31 + index * 53) * 0.5;
  const dt = (fract(local) - fireAt) * neuron.interval;
  return dt >= 0 ? dt : undefined;
};

/** The point `distance` pixels along a polyline, or undefined past its end. */
const alongPolyline = (points: Point2[], distance: number): Point2 | undefined => {
  let remaining = distance;
  for (let k = 1; k < points.length; k++) {
    const a = points[k - 1];
    const b = points[k];
    const segment = Math.hypot(b.x - a.x, b.y - a.y);
    if (remaining <= segment) {
      const f = segment === 0 ? 0 : remaining / segment;
      return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
    }
    remaining -= segment;
  }
  return undefined;
};

const NEURITE_WIDTH = [1.7, 1.1, 0.7];
// Neurons shallower than this sit outside the focal plane, with a crossfade
// either side as they drift through it.
const NEURON_FOCUS_DEPTH = 0.55;
const NEURON_FOCUS_BAND = 0.08;

/** A neuron's slow wander about its home: an offset in reference pixels and a
 *  change in depth, on unrelated periods so the path never closes. */
const neuronDrift = (seed: number, time: number) => ({
  dx: 22 * Math.sin((time * TAU) / 97 + seed) + 10 * Math.sin((time * TAU) / (97 * PHI) + seed * 2),
  dy:
    14 * Math.sin((time * TAU) / (97 * SQRT2) + seed * 3) + 6 * Math.sin((time * TAU) / 61 + seed),
  depth:
    0.1 * Math.sin((time * TAU) / 113 + seed * 5) +
    0.04 * Math.sin((time * TAU) / (113 * PHI) + seed),
});

const drawNeuron = (
  ctx: CanvasRenderingContext2D,
  neuron: Neuron,
  target: Neuron,
  index: number,
  width: number,
  height: number,
  time: number,
  alpha: number
) => {
  const scale = (height / REFERENCE_HEIGHT) * (1.05 + 0.75 * neuron.depth);
  const opacity = (0.3 + 0.6 * neuron.depth) * alpha;
  const soma = { x: neuron.x * width, y: neuron.y * height, r: 6.5 * scale };
  const dx = (target.x - neuron.x) * width;
  const dy = (target.y - neuron.y) * height;
  const axonAngle = Math.atan2(dy, dx) + neuron.axonJitter;
  const axonBranches: NeuriteBranch[] = [
    {
      parent: -1,
      angle: 0,
      length: (Math.hypot(dx, dy) * 0.55) / scale,
      curvature: neuron.axonCurvature,
      level: 0,
    },
    ...neuron.terminals,
  ];
  const traceFromSoma = (branches: NeuriteBranch[], seed: number) =>
    traceBranches(branches, soma, soma.r * 0.8, axonAngle, scale, time, seed);
  const dendrites = traceFromSoma(neuron.dendrites, neuron.seed);
  const axon = traceFromSoma(axonBranches, neuron.seed + 5);
  const dt = sinceFiring(neuron, index, time);
  const flash = dt === undefined ? 0 : Math.exp(-dt * 1.4);

  ctx.lineCap = "round";
  ctx.strokeStyle = LAVENDER;
  for (let level = 0; level < NEURITE_WIDTH.length; level++) {
    ctx.lineWidth = NEURITE_WIDTH[level] * scale;
    // The tree brightens briefly as the neuron fires.
    ctx.globalAlpha = opacity * (0.55 - level * 0.12) * (1 + flash * 0.6);
    ctx.beginPath();
    for (const [b, branch] of neuron.dendrites.entries()) {
      if (branch.level === level) {
        strokePolyline(ctx, dendrites[b].points);
      }
    }
    for (const [b, branch] of axonBranches.entries()) {
      if (branch.level === level) {
        strokePolyline(ctx, axon[b].points);
      }
    }
    ctx.stroke();
  }

  // Synaptic inputs twinkle at the dendrite tips.
  ctx.fillStyle = ORCHID;
  for (const [b, branch] of neuron.dendrites.entries()) {
    if (branch.level === 2) {
      const tip = dendrites[b].points[NEURITE_STEPS];
      const twinkle = Math.max(0, Math.sin(time * (0.2 + 0.3 * hash01(b + index * 7)) + b * 2.1));
      ctx.globalAlpha = opacity * (0.15 + 0.5 * twinkle ** 4);
      dot(ctx, tip.x, tip.y, 1.1 * scale);
    }
  }

  ctx.fillStyle = VIOLET;
  ctx.globalAlpha = opacity * (0.1 + 0.3 * flash);
  dot(ctx, soma.x, soma.y, soma.r * (2.2 + flash * 1.5));
  ctx.beginPath();
  traceSoma(ctx, soma, time, neuron.seed);
  ctx.globalAlpha = opacity * (0.35 + 0.5 * flash);
  ctx.fill();
  ctx.strokeStyle = WHITE;
  ctx.lineWidth = scale;
  ctx.globalAlpha = opacity * (0.5 + 0.4 * flash);
  ctx.stroke();

  // The action potential runs down the axon, then fades into the terminals.
  if (dt !== undefined) {
    const travelled = dt * SPIKE_SPEED * scale;
    const terminals = axon.slice(1);
    const axonLength = axonBranches[0].length * scale;
    const trail = 10;
    for (let k = trail; k >= 0; k--) {
      const distance = travelled - k * 2.2 * scale;
      if (distance < 0) {
        continue;
      }
      const fade = 1 - k / trail;
      const onAxon = alongPolyline(axon[0].points, distance);
      const positions = onAxon
        ? [onAxon]
        : terminals.flatMap((t) => alongPolyline(t.points, distance - axonLength) ?? []);
      ctx.fillStyle = k === 0 ? WHITE : ORCHID;
      ctx.globalAlpha = 0.85 * fade * fade * opacity;
      for (const p of positions) {
        dot(ctx, p.x, p.y, (k === 0 ? 1.9 : 1.2) * scale);
      }
    }
  }
  ctx.globalAlpha = 1;
};

const neuronsScene = (): HeroScene => {
  const random = seededRandom(Math.floor(Math.random() * 2 ** 31));
  let neurons: Neuron[] = [];
  let laidOutFor = 0;

  const layout = (count: number): Neuron[] =>
    Array.from({ length: count }, (_, i) => ({
      x: (i + 0.2 + random() * 0.6) / count,
      y: 0.2 + random() * 0.65,
      depth: 0.35 + random() * 0.65,
      dendrites: growDendrites(random),
      target: i + 1 < count ? i + 1 : i - 1,
      axonCurvature: (random() - 0.5) * 0.9,
      axonJitter: (random() - 0.5) * 0.5,
      terminals: growTerminals(random),
      interval: 8 + random() * 8,
      seed: random() * TAU,
    }));

  return {
    draw: (layers, width, height, time) => {
      const count = Math.max(3, Math.min(6, Math.round(width / 300)));
      if (count !== laidOutFor) {
        neurons = layout(count);
        laidOutFor = count;
      }
      drawMotes(layers, width, height, time, Math.round(width / 30));
      const scale = height / REFERENCE_HEIGHT;
      const placed = neurons.map((neuron) => {
        const drift = neuronDrift(neuron.seed, time);
        return {
          ...neuron,
          x: neuron.x + (drift.dx * scale) / width,
          y: neuron.y + (drift.dy * scale) / height,
          depth: Math.min(1, Math.max(0.25, neuron.depth + drift.depth)),
        };
      });
      for (const [index, neuron] of placed.entries()) {
        const target = placed[neuron.target];
        // A neuron crossing the focal plane is drawn on both, crossfading.
        const sharp = smoothstep(
          NEURON_FOCUS_DEPTH - NEURON_FOCUS_BAND,
          NEURON_FOCUS_DEPTH + NEURON_FOCUS_BAND,
          neuron.depth
        );
        if (sharp < 1) {
          drawNeuron(layers.far, neuron, target, index, width, height, time, 1 - sharp);
        }
        if (sharp > 0) {
          drawNeuron(layers.near, neuron, target, index, width, height, time, sharp);
        }
      }
    },
  };
};

/** Factories, since a scene can hold layout state for the canvas it paints. */
export const HERO_SCENES: Record<HeroBackgroundTheme, () => HeroScene> = {
  helix: helixScene,
  cells: cellsScene,
  neurons: neuronsScene,
  signals: signalsScene,
  still: stillScene,
};
